import { useReactiveVar } from "@apollo/client";
import {
  EnvelopeIcon,
  ExclamationCircleIcon,
  GlobeAltIcon,
  PhoneIcon,
} from "@heroicons/react/20/solid";
import { currentConfigVar} from "../../apollo/cache/config";
import lodash from "lodash";
import moment from "moment";
import numeral, { localeData } from "numeral";
import { QRCodeSVG } from "qrcode.react";
import { forwardRef, type LegacyRef } from "react";

localeData().currency.symbol = "GHS";

interface NetBillViewProps {
  ref?: LegacyRef<HTMLDivElement>;
  bill: {
    code: string;
    billingCycle: { _id: string };

    servicePoint: { _id: string };
    servicePointCode: string;
    servicePointStatus: string;
    region: { _id: string };
    regionCode: string;
    regionName: string;
    district: { _id: string };
    districtCode: string;
    districtName: string;
    block: { _id: string };
    blockCode: string;
    blockName: string;
    round: { _id: string };
    roundCode: string;
    roundName: string;
    plot: { _id: string };
    plotCode: string;
    plotName: string;
    customer: { _id: string };
    customerCode: string;
    customerType: string;
    customerRepresentative: {
      fullName: string;
      phoneNumber: string;
    };
    customerOrganization?: {
      name: string;
    };
    account: { _id: string };
    accountCode: string;
    accountType: string;
    ministry: { _id: string };
    ministryCode: string;
    ministryName: string;
    mda: { _id: string };
    mdaCode: string;
    mdaName: string;
    payer: {
      fullName: string;
      primaryPhoneNumber: string;
      primaryEmailAddress: string;
      phoneNumbers: string[];
      emailAddresses: string[];
    };
    physicalMeterCode: string;
    meter: { _id: string };
    meterCode: string;
    meterModelType: string;
    meterModelPhase: string;

    tariffClass: { _id: string };
    tariffClassCode: string;
    tariffClassName: string;
    serviceType: string;
    serviceClass: string;
    geoCode: string;
    community: string;
    address: string;
    ghanaPostAddress: string;
    activity: { _id: string };
    subActivity: { _id: string };
    extraAttributes: {
      _id: string;
      name: string;
      value: string;
    }[];

    month: number;
    year: number;

    consumptionValue: number;
    consumptionStartValue: number;
    consumptionEndValue: number;
    consumptionExportValue: number;
    consumptionExportStartValue: number;
    consumptionExportEndValue: number;
    consumptionApparentValue: number;
    consumptionApparentStartValue: number;
    consumptionApparentEndValue: number;
    consumptionMaxDemandEndValue: number;
    consumptionPeriod: number;
    consumptionStartDate: Date;
    consumptionEndDate: Date;
    consumptionStartType: string;
    consumptionEndType: string;

    billDate: Date;
    billPeriod: number;
    billStartDate: Date;
    billEndDate: Date;
    billAvgConsumptionValue: number;
    billConsumptionValue: number;
    billAmount: number;

    bills: {
      currentDate: Date;
      previousDate: Date;
      tariffInstance: { _id: string };
      numberOfDays: number;
      consumption: number;
      billItems: {
        title: string;
        displayTitle: string;
        unit: string;
        quantity: string;
        unitCost: number;
        formattedUnitCost: string;
        cost: number;
      }[];
      billAmount: number;
    }[];
    importBills: {
      currentDate: Date;
      previousDate: Date;
      tariffInstance: { _id: string };
      numberOfDays: number;
      consumption: number;
      billItems: {
        title: string;
        displayTitle: string;
        unit: string;
        quantity: string;
        unitCost: number;
        formattedUnitCost: string;
        cost: number;
      }[];
      billAmount: number;
    }[];
    exportBills: {
      currentDate: Date;
      previousDate: Date;
      tariffInstance: { _id: string };
      numberOfDays: number;
      consumption: number;
      billItems: {
        title: string;
        displayTitle: string;
        unit: string;
        quantity: string;
        unitCost: number;
        formattedUnitCost: string;
        cost: number;
      }[];
      billAmount: number;
    }[];
    extraBills: {
      currentDate: Date;
      previousDate: Date;
      tariffInstance: { _id: string };
      numberOfDays: number;
      consumption: number;
      billItems: {
        title: string;
        displayTitle: string;
        unit: string;
        quantity: string;
        unitCost: number;
        formattedUnitCost: string;
        cost: number;
      }[];
      billAmount: number;
    }[];
    adjustments?: {
      currentDate: Date;
      previousDate: Date;
      tariffInstance: { _id: string };
      numberOfDays: number;
      consumption: number;
      billItems: {
        title: string;
        displayTitle: string;
        unit: string;
        quantity: string;
        unitCost: number;
        formattedUnitCost: string;
        cost: number;
      }[];
      billAmount: number;
    }[];

    adjustmentPeriod: number;
    adjustmentStartDate: Date;
    adjustmentEndDate: Date;
    adjustmentActualValue: number;
    adjustmentEstimatedValue: number;
    adjustmentValue: number;
    adjustmentActualAmount: number;
    adjustmentEstimatedAmount: number;
    adjustmentAmount: number;

    outstandingAmount: number;

    payments?: {
      date: Date;
      amount: number;
      status: string;
      reference: string;
    }[];
    paymentsAmount: number;
    previousPayments?: {
      date: Date;
      amount: number;
      status: string;
      reference: string;
    }[];

    consumptionHistory: {
      month: number;
      value: number;
      exportValue: number;
    }[];
    consumptionHistoryMax: number;

    netAmount: number;
    outstandingNetAmount: number;
    netAmountDue: number;
    netCycle: number;
    importAmount: number;
    exportAmount: number;
    extraAmount: number;

    meta: {
      isSettlementCycle: boolean;
    }

    amountDue: number;
    amountDueAt: Date;
    createdAt: Date;
    updatedAt: Date;
  };
}

export const NetBillView = forwardRef<HTMLDivElement, NetBillViewProps>(
  ({ bill }, ref) => {
    const { dateFormat, moneyFormat } = useReactiveVar(currentConfigVar);

    return (
      <div ref={ref} className="text-black">
        <div
          // style={{ breakAfter: "always" }}
          className="w-[1240px] overflow-hidden flex flex-col "
        >
          <div className="border-gray-300 border p-6 border-dashed bg-gray-50 relative flex-1 overflow-hidden flex flex-col">
            <div
              className="absolute flex flex-col items-center justify-center top-0 right-6 w-40 h-40 bg-no-repeat bg-cover"
              style={{
                backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="160" height="160"><polygon points="0,0 160,0 160,160 80,140 0,160" fill="%2316a34a" fill-rule="evenodd" stroke-width="0"/></svg>')`,
              }}
            >
              <span className="text-white text-xl font-semibold">
                {moment(bill.billEndDate).format("MMMM").toUpperCase()}
              </span>
              <span className="text-white text-xl font-semibold">
                {moment(bill.billEndDate).format("YYYY").toUpperCase()}
              </span>
              <span className="text-ecg-secondary-300 text-4xl font-semibold">
                BILL
              </span>
            </div>

            <div className="flex flex-row items-center space-x-4 justify-left">
              <div className="bg-green-600 p-1.5">
                <img
                  alt="ECG Logo"
                  src={
                    "https://www.ecg.com.gh/images/site/official_ecg_logo.jpg"
                  }
                  className="border-2 border-white bg-green-600  h-20 w-20"
                />
              </div>
              <div className="space-y-1">
                <h1 className="text-xl text-green-600 font-bold">
                  ELECTRICITY COMPANY OF GHANA LIMITED
                </h1>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <EnvelopeIcon className="h-4 w-4" />
                    <span>P.O BOX GP 521, ACCRA</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <PhoneIcon className="h-4 w-4" />
                    <span>0302-611-611</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <GlobeAltIcon className="h-4 w-4" />
                    <span>ecgho@ecggh.com</span>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold">VAT REG:</span>
                    <span>714V000395</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold">Website:</span>
                    <span>https://www.ecg.com.gh</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-gray-300 divide-y divide-gray-300 mt-4">
              <div className="bg-green-50 px-3 py-1.5">
                <span className="text-md font-bold">Customer Information</span>
              </div>
              <div className="grid grid-cols-6 divide-x divide-gray-300 divide-dashed">
                <div className="col-span-4 grid grid-cols-4 divide-y divide-gray-300 divide-dashed">
                  <div className="col-span-4 grid grid-cols-4 divide-x divide-gray-300 divide-dashed">
                    <div className="col-span-2 grid grid-cols-2 gap-2 p-3 py-2 pb-1">
                      <div className="col-span-2">
                        <span className="block text-md">Customer Name</span>
                        <div className="block w-full text-md font-medium">
                          {bill.customerOrganization?.name ||
                            bill.customerRepresentative.fullName ||
                            "N/A"}
                        </div>
                      </div>
                      <div>
                        <span className="block text-md">Phone Number</span>
                        <div className="block w-full text-md font-medium">
                          {bill.customerRepresentative.phoneNumber || "N/A"}
                        </div>
                      </div>
                      <div>
                        <span className="block text-md">Digital Address</span>
                        <div className="block w-full text-md font-medium">
                          {bill.ghanaPostAddress || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2 grid grid-cols-9 gap-2 p-3 py-2">
                      <div className="col-span-4">
                        <span className="block text-md">Meter Number</span>
                        <div className="block w-full text-md font-medium">
                          {bill.meterCode || "N/A"}
                        </div>
                      </div>
                      <div className="col-span-5">
                        <span className="block text-md">Geo Code</span>
                        <div className="block w-full text-md font-medium">
                          {bill.geoCode || "N/A"}
                        </div>
                      </div>
                      <div className="col-span-4">
                        <span className="block text-md">Account Number</span>
                        <div className="block w-full text-md font-medium">
                          {bill.accountCode || "N/A"}
                        </div>
                      </div>
                      <div className="col-span-5">
                        <span className="block text-md">Tariff</span>
                        <div className="block w-full text-md font-medium">
                          {bill.tariffClassCode} - {bill.tariffClassName}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-4 p-3 py-2 pt-1 ">
                    <span className="block text-md">Service Address</span>
                    <div className="block w-full text-md font-medium">
                      <p className=" line-clamp-1">{bill.address || "N/A"}</p>
                    </div>
                  </div>
                </div>
                <div className="col-span-2 grid grid-cols-2 gap-2 p-3 py-2">
                  <div className="col-span-2">
                    <span className="block text-md">Bill Period</span>
                    <div className="block w-full text-md font-medium">
                      {moment(bill.billStartDate).format(dateFormat)} -{" "}
                      {moment(bill.billEndDate).format(dateFormat)} (
                      {numeral(bill.billPeriod).format("0,0")} days)
                    </div>
                  </div>
                  <div>
                    <span className="block text-md">Bill Number</span>
                    <div className="block w-full text-md font-medium">
                      {bill.code.replace("NAMR", "").replace("AMR", "") ||
                        "N/A"}
                    </div>
                  </div>
                  <div>
                    <span className="block text-md">Issuing Center</span>
                    <div className="block w-full text-md font-medium">
                      {bill.districtName || "N/A"}
                    </div>
                  </div>
                  <div>
                    <span className="block text-md">Issue Date</span>
                    <div className="block w-full text-md font-medium">
                      {moment(bill.billDate).format(dateFormat)}
                    </div>
                  </div>
                  <div>
                    <span className="block text-md">Due Date</span>
                    <div className="block w-full text-md font-medium">
                      {moment(bill.amountDueAt).format(dateFormat)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 px-3 py-1.5">
                <span className="text-md font-bold">
                  Consumption Information
                </span>
              </div>
              <div className="border-t border-gray-300">
                <table className="w-full border-collapse table-fixed">
                  <thead>
                    <tr className="bg-green-600  border-b border-gray-300">
                      <th
                        scope="col"
                        colSpan={2}
                        className="px-3 py-2 text-left text-md font-semibold text-white w-[25%]"
                      >
                        Energy Type
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-right text-md font-semibold text-white whitespace-nowrap w-[15%]"
                      >
                        Prev. Reading
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-right text-md font-semibold text-white whitespace-nowrap w-[15%]"
                      >
                        Curr. Reading
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-right text-md font-semibold text-white whitespace-nowrap w-[15%]"
                      >
                        Difference
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-right text-md font-semibold text-white whitespace-nowrap w-[15%]"
                      >
                        Multiplier
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-right text-md font-semibold text-white w-[15%]"
                      >
                        Consumption
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200 bg-ecg-secondary-50">
                      <td colSpan={6} className="py-1.5 px-3 text-sm">
                        {moment(bill.consumptionStartDate).format(dateFormat)} -{" "}
                        {moment(bill.consumptionEndDate).format(dateFormat)} (
                        {numeral(bill.consumptionPeriod).format("0,0")} days)
                      </td>
                      <td className="py-1.5 px-3 text-sm text-right">
                        {bill.consumptionEndType === "E"
                          ? "Estimated"
                          : "Actual"}
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200 last:border-0">
                      <td colSpan={2} className="px-3 py-2 text-md font-medium">
                        Active Energy (kWh)
                      </td>
                      <td className="px-3 py-2 text-right text-md">
                        {numeral(bill.consumptionStartValue).format("0,0.00")}
                      </td>
                      <td className="px-3 py-2 text-right text-md">
                        {numeral(bill.consumptionEndValue).format("0,0.00")}
                      </td>
                      <td className="px-3 py-2 text-right text-md">
                        {numeral(bill.consumptionValue).format("0,0.00")}
                      </td>
                      <td className="px-3 py-2 text-right text-md">1</td>
                      <td className="px-3 py-2 text-right text-md">
                        {numeral(bill.consumptionValue).format("0,0.00")}
                      </td>
                    </tr>
                    {bill.serviceClass === "Industrial" && (
                      <>
                        <tr className="border-b border-gray-200">
                          <td
                            colSpan={2}
                            className="px-3 py-2 text-md font-medium"
                          >
                            Apparent Energy (kVAh)
                          </td>
                          <td className="px-3 py-2 text-right text-md">
                            {numeral(bill.consumptionApparentStartValue).format(
                              "0,0.00",
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-md">
                            {numeral(bill.consumptionApparentEndValue).format(
                              "0,0.00",
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-md">
                            {numeral(bill.consumptionApparentValue).format(
                              "0,0.00",
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-md">1</td>
                          <td className="px-3 py-2 text-right text-md">
                            {numeral(bill.consumptionApparentValue).format(
                              "0,0.00",
                            )}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td
                            colSpan={2}
                            className="px-3 py-2 text-md font-medium"
                          >
                            Max Demand (kVA)
                          </td>
                          <td className="px-3 py-2 text-right text-md">
                            {numeral(0).format("0,0.0000")}
                          </td>
                          <td className="px-3 py-2 text-right text-md">
                            {numeral(bill.consumptionMaxDemandEndValue).format(
                              "0,0.00",
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-md">
                            {numeral(bill.consumptionMaxDemandEndValue).format(
                              "0,0.00",
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-md">1</td>
                          <td className="px-3 py-2 text-right text-md">
                            {numeral(bill.consumptionMaxDemandEndValue).format(
                              "0,0.00",
                            )}
                          </td>
                        </tr>
                        <tr className="border-0">
                          <td
                            colSpan={6}
                            className="px-3 py-2 text-md font-medium"
                          >
                            Power Factor
                          </td>
                          <td className="px-3 py-2 text-right text-md">
                            {numeral(
                              bill.consumptionValue /
                                bill.consumptionApparentValue,
                            ).format("0,0.00")}
                          </td>
                        </tr>
                      </>
                    )}
                    <tr className="border-b border-gray-200 last:border-0">
                      <td colSpan={2} className="px-3 py-2 text-md font-medium">
                        Export Energy (kWh)
                      </td>
                      <td className="px-3 py-2 text-right text-md">
                        {numeral(bill.consumptionExportStartValue).format(
                          "0,0.00",
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-md">
                        {numeral(bill.consumptionExportEndValue).format(
                          "0,0.00",
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-md">
                        {numeral(bill.consumptionExportValue).format("0,0.00")}
                      </td>
                      <td className="px-3 py-2 text-right text-md">1</td>
                      <td className="px-3 py-2 text-right text-md">
                        {numeral(bill.consumptionExportValue).format("0,0.00")}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex-1 mt-4 grid grid-cols-5">
              <div className="col-span-2 mr-4 flex flex-col space-y-4">
                <div className="border border-gray-300">
                  <div className="flex items-center justify-center bg-green-50 px-3 py-1.5 border-b border-gray-300">
                    <span className="text-md font-bold text-center">
                      Consumption History (12 Months)
                    </span>
                  </div>
                  <div className="w-full h-64 shrink-0 flex flex-col p-2">
                    <div className="flex-1 flex flex-row justify-between">
                      <div className="flex items-center w-5 p-0">
                        <span className="text-md transform -rotate-90 -ml-1.5 font-semibold">
                          kWh
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <div className="flex-1 flex flex-col-reverse">
                          {lodash.times(6, (i) => (
                            <div className="flex-1 flex items-end justify-end">
                              <span className="text-xs text-gray-700">
                                {(lodash.ceil(bill.consumptionHistoryMax, -1) /
                                  5) *
                                  i}{" "}
                                -
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="h-6" />
                      </div>
                      {lodash.times(12, (index) => {
                        const month = ((index + bill.month) % 12) + 1;
                        const monthConsumption = lodash
                          .chain(bill.consumptionHistory)
                          .keyBy("month")
                          .get(month)
                          .get("value")
                          .value();
                        const monthExportConsumption = lodash
                          .chain(bill.consumptionHistory)
                          .keyBy("month")
                          .get(month)
                          .get("exportValue")
                          .value();
                        return (
                          <div className="flex flex-col items-center">
                            <div className="flex-1 flex overflow-hidden">
                              <div className="rounded-full bg-amber-50 w-2 flex-1 flex items-end">
                                <div
                                  className="rounded-full bg-amber-600 w-full"
                                  style={{
                                    height: `${
                                      (monthConsumption /
                                        lodash.ceil(
                                          bill.consumptionHistoryMax,
                                          -1,
                                        )) *
                                      100
                                    }%`,
                                  }}
                                />
                              </div>
                              <div className="bg-green-50 w-2 flex-1 flex items-end">
                                <div
                                  className="rounded-full rounded-full bg-green-600 w-full"
                                  style={{
                                    height: `${
                                      (monthExportConsumption /
                                        lodash.ceil(
                                          bill.consumptionHistoryMax,
                                          -1,
                                        )) *
                                      100
                                    }%`,
                                  }}
                                />
                              </div>
                            </div>
                            <div className="h-6 flex items-end justify-center">
                              <span className="text-xs text-gray-700">
                                {lodash.padStart(`${month}`, 2, "00")}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-center mt-1.5 pl-12">
                      <span className="text-md font-semibold">Month</span>
                    </div>
                  </div>
                </div>
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <div className="border border-gray-300">
                      <div className="flex items-center justify-center bg-green-50 px-3 py-1.5">
                        <span className="text-md font-bold text-center">
                          Last 5 Payments
                        </span>
                      </div>
                      <div className="">
                        <table className="min-w-full border-t border-gray-300 border-collapse ">
                          <thead>
                            <tr className="bg-green-600 border-b border-gray-300">
                              <th
                                scope="col"
                                colSpan={2}
                                className="px-3 py-2 text-left text-md font-semibold text-white w-[40%]"
                              >
                                Date
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 text-right text-md font-semibold text-white w-[20%]"
                              >
                                Amount (GHS)
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-gray-200 bg-ecg-secondary-50">
                              <td
                                colSpan={3}
                                className="py-1.5 px-3 text-sm text-gray-600"
                              >
                                Current Month
                              </td>
                            </tr>
                            {bill.payments?.length ? (
                              bill.payments.map((payment, idx) => (
                                <tr
                                  key={idx}
                                  className="border-b border-gray-200 last:border-0"
                                >
                                  <td
                                    colSpan={2}
                                    className="px-3 py-2 text-md font-medium"
                                  >
                                    {moment(payment.date).format(dateFormat)}
                                  </td>
                                  <td className="px-3 py-2 text-right text-md">
                                    {numeral(payment.amount).format("0,0.00")}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr className="border-b border-gray-200">
                                <td colSpan={5} className="px-3 py-2 text-md">
                                  No Payments Were Made In The Current Month
                                </td>
                              </tr>
                            )}
                            {Boolean(bill.previousPayments?.length) && (
                              <>
                                <tr className="border-b border-gray-200 bg-ecg-secondary-50">
                                  <td
                                    colSpan={3}
                                    className="py-1.5 px-3 text-sm"
                                  >
                                    Previous Month
                                  </td>
                                </tr>
                                {bill.previousPayments?.map((payment, idx) => (
                                  <tr
                                    key={idx}
                                    className="border-b border-gray-200 last:border-0"
                                  >
                                    <td
                                      colSpan={2}
                                      className="px-3 py-2 text-md font-medium"
                                    >
                                      {moment(payment.date).format(dateFormat)}
                                    </td>
                                    <td className="px-3 py-2 text-right text-md">
                                      {numeral(payment.amount).format("0,0.00")}
                                    </td>
                                  </tr>
                                ))}
                              </>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="border border-gray-300 mt-4">
                      <div className="flex items-center justify-center bg-green-50  px-3 py-1.5">
                        <span className="text-md font-bold text-center">
                          Net Bill Ledger
                        </span>
                      </div>

                      <div>
                        <table className="w-full border-t border-gray-300 border-collapse table-fixed">
                          <tbody>
                            <tr className="border-b border-gray-200 last:border-0">
                              <td
                                colSpan={2}
                                className="px-3 py-2 text-md font-medium whitespace-nowrap"
                              >
                                Current Net Charge (Import - Export)
                              </td>
                              <td className="px-3 py-2 text-right text-md whitespace-nowrap">
                                {numeral(bill?.netAmount).format("$ 0,0.00")}
                              </td>
                            </tr>
                            <tr className="border-b border-gray-200 last:border-0">
                              <td
                                colSpan={2}
                                className="px-3 py-2 text-md font-medium whitespace-nowrap"
                              >
                                Previous Net Balance
                              </td>
                              <td className="px-3 py-2 text-right text-md whitespace-nowrap">
                                {numeral(bill?.outstandingNetAmount).format(
                                  "$ 0,0.00",
                                )}
                              </td>
                            </tr>
                            <tr className="border-b border-gray-200 last:border-0">
                              <td
                                colSpan={2}
                                className="px-3 py-2 text-md font-medium whitespace-nowrap"
                              >
                                Current Net Balance
                              </td>
                              <td className="px-3 py-2 text-right text-md whitespace-nowrap">
                                {numeral(bill.netAmountDue).format("$ 0,0.00")}
                              </td>
                            </tr>
                            <tr className="border-b border-gray-200 last:border-0">
                              <td
                                colSpan={2}
                                className="px-3 py-2 text-md font-medium whitespace-nowrap"
                              >
                                Current Net Cycle
                              </td>
                              <td className="px-3 py-2 text-right text-md whitespace-nowrap">
                                {numeral(bill.netCycle).format("0,0")}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="bg-red-100 mt-4 px-4 py-5 flex items-center justify-center">
                      <span className="text-lg text-red-500 inline-flex">
                        <ExclamationCircleIcon className="h-6 w-6 mr-2" />
                        Payment due by
                        <span className="font-semibold ml-1">
                          {moment(bill.amountDueAt).format("Do MMMM, YYYY")}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-span-3 flex flex-col space-y-4">
                <div className="border border-gray-300">
                  <div className="flex items-center justify-center bg-amber-50 px-3 py-1.5">
                    <span className="text-md font-bold text-center">
                      Bill Calculation (Import Consumption)
                    </span>
                  </div>
                  <div className="border-t border-gray-300">
                    <table className="w-full border-collapse table-fixed">
                      <thead>
                        <tr className="bg-amber-600  border-b border-gray-300">
                          <th
                            scope="col"
                            colSpan={2}
                            className="px-3 py-2 text-left text-md font-semibold text-white w-[34%]"
                          >
                            Items
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 text-right text-md font-semibold text-white  w-[22%]"
                          >
                            Units
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 text-right text-md font-semibold text-white  w-[22%]"
                          >
                            Price
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 text-right text-md font-semibold text-white  w-[22%]"
                          >
                            Amount (GHS)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {bill?.importBills?.map((billX) => (
                          <>
                            <tr className="border-b border-gray-200 bg-ecg-secondary-50">
                              <td colSpan={5} className="py-1.5 px-3 text-sm">
                                {moment(billX.previousDate).format(dateFormat)}{" "}
                                - {moment(billX.currentDate).format(dateFormat)}{" "}
                                ({numeral(billX.numberOfDays).format("0,0")}{" "}
                                days)
                              </td>
                            </tr>
                            {billX.billItems.map((item, idx) => (
                              <tr
                                key={idx}
                                className="border-b border-gray-200 last:border-0"
                              >
                                <td
                                  colSpan={2}
                                  className="px-3 py-2 text-md font-medium whitespace-nowrap"
                                >
                                  {item.title}
                                </td>
                                <td className="px-3 py-2 text-right text-md whitespace-nowrap">
                                  {item.unit}
                                </td>
                                <td className="px-3 py-2 text-right text-md whitespace-nowrap">
                                  {numeral(item.unitCost).format(moneyFormat)}
                                </td>
                                <td className="px-3 py-2 text-right text-md whitespace-nowrap">
                                  {numeral(item.cost).format("0,0.00")}
                                </td>
                              </tr>
                            ))}
                          </>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-amber-600">
                          <th
                            scope="col"
                            colSpan={4}
                            className="px-3 py-2.5 text-left text-md font-semibold text-white w-[40%]"
                          >
                            Total Import Consumption Amount
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2.5 text-right text-md font-semibold text-white whitespace-nowrap w-[20%]"
                          >
                            {numeral(bill.importAmount).format("$ 0,0.00")}
                          </th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
                <div className="border border-gray-300">
                  <div className="flex items-center justify-center bg-green-50 px-3 py-1.5">
                    <span className="text-md font-bold text-center">
                      Bill Calculation (Export Consumption)
                    </span>
                  </div>
                  <div className="border-t border-gray-300">
                    <table className="w-full border-collapse table-fixed">
                      <thead>
                        <tr className="bg-green-600  border-b border-gray-300">
                          <th
                            scope="col"
                            colSpan={2}
                            className="px-3 py-2 text-left text-md font-semibold text-white w-[34%]"
                          >
                            Items
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 text-right text-md font-semibold text-white  w-[22%]"
                          >
                            Units
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 text-right text-md font-semibold text-white  w-[22%]"
                          >
                            Price
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 text-right text-md font-semibold text-white  w-[22%]"
                          >
                            Amount (GHS)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {bill?.exportBills?.map((billX) => (
                          <>
                            <tr className="border-b border-gray-200 bg-ecg-secondary-50">
                              <td colSpan={5} className="py-1.5 px-3 text-sm">
                                {moment(billX.previousDate).format(dateFormat)}{" "}
                                - {moment(billX.currentDate).format(dateFormat)}{" "}
                                ({numeral(billX.numberOfDays).format("0,0")}{" "}
                                days)
                              </td>
                            </tr>
                            {billX.billItems.map((item, idx) => (
                              <tr
                                key={idx}
                                className="border-b border-gray-200 last:border-0"
                              >
                                <td
                                  colSpan={2}
                                  className="px-3 py-2 text-md font-medium whitespace-nowrap"
                                >
                                  {item.title}
                                </td>
                                <td className="px-3 py-2 text-right text-md whitespace-nowrap">
                                  {item.unit}
                                </td>
                                <td className="px-3 py-2 text-right text-md whitespace-nowrap">
                                  {numeral(item.unitCost).format(moneyFormat)}
                                </td>
                                <td className="px-3 py-2 text-right text-md whitespace-nowrap">
                                  {numeral(item.cost).format("0,0.00")}
                                </td>
                              </tr>
                            ))}
                          </>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-green-600">
                          <th
                            scope="col"
                            colSpan={4}
                            className="px-3 py-2.5 text-left text-md font-semibold text-white w-[40%]"
                          >
                            Total Export Consumption Amount
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2.5 text-right text-md font-semibold text-white whitespace-nowrap w-[20%]"
                          >
                            {numeral(bill.exportAmount).format("$ 0,0.00")}
                          </th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
                <div className="border border-gray-300">
                  <div className="flex items-center justify-center bg-green-50 px-3 py-1.5">
                    <span className="text-md font-bold text-center">
                      Bill Calculation (Statutory Charges)
                    </span>
                  </div>
                  <div className="border-t border-gray-300">
                    <table className="w-full border-collapse table-fixed">
                      <thead>
                        <tr className="bg-green-600  border-b border-gray-300">
                          <th
                            scope="col"
                            colSpan={2}
                            className="px-3 py-2 text-left text-md font-semibold text-white w-[34%]"
                          >
                            Items
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 text-right text-md font-semibold text-white  w-[22%]"
                          >
                            Units
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 text-right text-md font-semibold text-white  w-[22%]"
                          >
                            Price
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 text-right text-md font-semibold text-white  w-[22%]"
                          >
                            Amount (GHS)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {bill?.extraBills?.map((billX) => (
                          <>
                            <tr className="border-b border-gray-200 bg-ecg-secondary-50">
                              <td colSpan={5} className="py-1.5 px-3 text-sm">
                                {moment(billX.previousDate).format(dateFormat)}{" "}
                                - {moment(billX.currentDate).format(dateFormat)}{" "}
                                ({numeral(billX.numberOfDays).format("0,0")}{" "}
                                days)
                              </td>
                            </tr>
                            {billX.billItems.map((item, idx) => (
                              <tr
                                key={idx}
                                className="border-b border-gray-200 last:border-0"
                              >
                                <td
                                  colSpan={2}
                                  className="px-3 py-2 text-md font-medium whitespace-nowrap"
                                >
                                  {item.title}
                                </td>
                                <td className="px-3 py-2 text-right text-md whitespace-nowrap">
                                  {item.unit}
                                </td>
                                <td className="px-3 py-2 text-right text-md whitespace-nowrap">
                                  {numeral(item.unitCost).format(moneyFormat)}
                                </td>
                                <td className="px-3 py-2 text-right text-md whitespace-nowrap">
                                  {numeral(item.cost).format("0,0.00")}
                                </td>
                              </tr>
                            ))}
                          </>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-green-600">
                          <th
                            scope="col"
                            colSpan={4}
                            className="px-3 py-2.5 text-left text-md font-semibold text-white w-[40%]"
                          >
                            Statutory Charges
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2.5 text-right text-md font-semibold text-white whitespace-nowrap w-[20%]"
                          >
                            {numeral(bill.extraAmount).format("$ 0,0.00")}
                          </th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                <div className="flex-1 flex flex-col justify-between">
                  <div className="border border-gray-300">
                    <div className="flex items-center justify-center bg-green-50  px-3 py-1.5">
                      <span className="text-md font-bold text-center">
                        Adjustment Information
                      </span>
                    </div>

                    {bill.adjustments?.length ? (
                      <div>
                        <table className="w-full border-t border-gray-300 border-collapse table-fixed">
                          <tbody>
                            {bill.adjustments.map((billX) => (
                              <>
                                <tr className="border-b border-gray-200 bg-ecg-secondary-50">
                                  <td
                                    colSpan={5}
                                    className="py-1.5 px-3 text-sm"
                                  >
                                    {moment(billX.previousDate).format(
                                      dateFormat,
                                    )}{" "}
                                    -{" "}
                                    {moment(billX.currentDate).format(
                                      dateFormat,
                                    )}{" "}
                                    ({numeral(billX.numberOfDays).format("0,0")}{" "}
                                    days)
                                  </td>
                                </tr>
                                {billX.billItems.map((item, idx) => (
                                  <tr
                                    key={idx}
                                    className="border-b border-gray-200 last:border-0"
                                  >
                                    <td
                                      colSpan={2}
                                      className="px-3 py-2 text-md font-medium whitespace-nowrap"
                                    >
                                      {item.title}
                                    </td>
                                    <td className="px-3 py-2 text-right text-md whitespace-nowrap">
                                      {item.unit}
                                    </td>
                                    <td className="px-3 py-2 text-right text-md whitespace-nowrap">
                                      {numeral(item.unitCost).format(
                                        moneyFormat,
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-right text-md whitespace-nowrap">
                                      {numeral(item.cost).format("0,0.00")}
                                    </td>
                                  </tr>
                                ))}
                              </>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div>
                        <table className="w-full divide-y divide-gray-300 border-t border-gray-300 border-collapse table-fixed">
                          <tbody>
                            <tr className="">
                              <td colSpan={5} className="py-2 px-3 text-md">
                                No Adjustments Were Made For This Bill Period
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-4 mt-4 items-end">
                    <div className="shrink-0">
                      <QRCodeSVG
                        value={`https://consumer.ecg.com.gh/bills/${bill.code}`}
                        size={230}
                        imageSettings={{
                          src: "https://www.ecg.com.gh/images/site/official_ecg_logo.jpg",
                          height: 30,
                          width: 30,
                          excavate: true,
                        }}
                      />
                    </div>
                    <table className="flex-1">
                      <tbody>
                        <tr className="">
                          <th
                            scope="row"
                            colSpan={3}
                            className="pl-4 pr-3  pb-1 text-left text-md font-medium"
                          >
                            Statutory Charges
                          </th>
                          <td className="pl-3 pr-4 pb-1 text-right text-md">
                            {numeral(bill.extraAmount).format("$ 0,0.00")}
                          </td>
                        </tr>
                        <tr className="">
                          <th
                            scope="row"
                            colSpan={3}
                            className="pl-4 pr-3  pb-1 text-left text-md font-medium"
                          >
                            Positive Net Charge
                          </th>
                          <td className="pl-3 pr-4 pb-1 text-right text-md">
                            {numeral(
                              lodash.max([
                                0,
                                bill?.meta?.isSettlementCycle
                                  ? bill?.netAmountDue
                                  : 0,
                              ]),
                            ).format("$ 0,0.00")}
                          </td>
                        </tr>
                        <tr>
                          <th
                            scope="row"
                            colSpan={3}
                            className="pl-4 pr-3 py-1 text-left text-md font-medium"
                          >
                            Previous Balance
                          </th>
                          <td className="pl-3 pr-4 py-1 text-right text-md">
                            {numeral(bill.outstandingAmount).format("$ 0,0.00")}
                          </td>
                        </tr>
                        <tr>
                          <th
                            scope="row"
                            colSpan={3}
                            className="pl-4 pr-3 py-1 text-left text-md font-medium"
                          >
                            Payments
                          </th>
                          <td className="pl-3 pr-4 py-1 text-right text-md">
                            {numeral(bill.paymentsAmount).format("$ 0,0.00")}
                          </td>
                        </tr>
                        <tr>
                          <th
                            scope="row"
                            colSpan={3}
                            className="pl-4 pr-3 py-1 pb-4 text-left text-md font-medium"
                          >
                            Adjustments
                          </th>
                          <td className="pl-3 pr-4 py-1 pb-4 text-right text-md">
                            {numeral(bill.adjustmentAmount).format("$ 0,0.00")}
                          </td>
                        </tr>
                        <tr>
                          <th
                            scope="row"
                            colSpan={3}
                            className="bg-green-600 pl-4 pr-3 py-4 text-left text-xl font-semibold text-white"
                          >
                            Amount Payable
                          </th>
                          <td className="bg-green-600 pl-3 pr-4 py-4 text-right text-3xl font-semibold text-white whitespace-nowrap">
                            {numeral(bill.amountDue).format("$ 0,0.00")}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="h-[220px] p-6 grid grid-cols-5">
            <div className="col-span-4 flex space-x-6">
              <QRCodeSVG
                value={`https://consumer.ecg.com.gh/bills/${bill.code}`}
                size={170}
                imageSettings={{
                  src: "https://www.ecg.com.gh/images/site/official_ecg_logo.jpg",
                  height: 25,
                  width: 25,
                  excavate: true,
                }}
              />
              <div className="flex-1 border border-gray-300 grid gap-1 p-4 py-3">
                <div className="inline-flex space-x-2">
                  <span className="text-md">Customer Name:</span>
                  <div className="text-md font-medium">
                    {bill.customerOrganization?.name ||
                      bill.customerRepresentative.fullName ||
                      "N/A"}
                  </div>
                </div>
                <div className="inline-flex space-x-2">
                  <span className="text-md">Account No:</span>
                  <div className="text-md font-medium">
                    {bill.accountCode || "N/A"}
                  </div>
                </div>
                <div className="inline-flex space-x-2">
                  <span className="text-md">Meter No:</span>
                  <div className="text-md font-medium">
                    {bill.meterCode || "N/A"}
                  </div>
                </div>
                <div className="inline-flex space-x-2">
                  <span className="text-md">Bill Number:</span>
                  <div className="text-md font-medium">
                    {bill.code.replace("NAMR", "") || "N/A"}
                  </div>
                </div>
                <div className="inline-flex space-x-2">
                  <span className="text-md">Issue Date:</span>
                  <div className="text-md font-medium">
                    {moment(bill.billDate).format(dateFormat)}
                  </div>
                </div>
              </div>
              <div className="space-y-3 flex flex-col">
                <div className="bg-green-600 min-w-[190px] flex-1 p-3 items-center justify-center text-center flex">
                  <div>
                    <span className="block text-lg text-white">
                      Amount Payable
                    </span>
                    <div className="mt-3 block w-full text-3xl font-semibold text-ecg-secondary-300 whitespace-nowrap">
                      <span className="text-xl">GHS</span>{" "}
                      {numeral(bill.amountDue).format("0,0.00")}
                    </div>
                  </div>
                </div>

                <div className="bg-red-100 p-3 flex items-center justify-center ">
                  <span className="text-md text-red-500 inline-flex">
                    <ExclamationCircleIcon className="h-5 w-5 mr-2" />
                    Payment due by
                    <span className="font-semibold ml-1">
                      {moment(bill.amountDueAt).format("Do MMMM, YYYY")}
                    </span>
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-center items-end px-6 py-2 text-md font-semibold">
              Page 1 of 1
            </div>
          </div>
        </div>
      </div>
    );
  },
);
